import { type MutableRefObject, useEffect, useRef } from 'react';

/**
 * Detect clicks outside of given element
 *
 * IMPORTANT: `Overlay` component uses similar logic but slightly differently - it detects
 * mouse events on backdrop (so this hook is not suitable). When changing mouse event logic
 * for outside clicks, keep these two consistent as much as possible.
 *
 * Idea shamelessly stolen from https://usehooks.com/useClickAway/
 */
export function useClickAway<T extends HTMLElement>(
  callback: (event: MouseEvent) => void,
  externalRef?: MutableRefObject<T | null>, // Allow external ref
) {
  const internalRef = useRef<T | null>(null); // Create internal ref if needed
  const ref = externalRef || internalRef; // Use external ref if provided

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback(event);
      }
    }

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [callback, ref]);

  return ref; // Return internal ref only if no external ref is passed
}
